import { useMemo, useRef, useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Avatar, Box, Button, Flex, HStack, Icon, Input, InputGroup, InputLeftElement, Link, Progress, SimpleGrid, Text, Tooltip } from '@chakra-ui/react';
import { RiAddLine, RiSearchLine, RiRefreshLine, RiMegaphoneLine, RiPlayLine, RiTimeLine, RiErrorWarningLine, RiCheckLine } from 'react-icons/ri';
import { useListCampaignsQuery, useCreateCampaignMutation } from 'api/metaWhatsappApi';
import useMetaWhatsappSocket from 'views/admin/meta-whatsapp/hooks/useMetaWhatsappSocket';
import useAppToast from 'components/toast';
import campaignError from './campaignErrors';
import { CampaignPage, CampaignCard, StatCard, StatusBadge } from './_ui/components';
import { DataTable } from './_ui/DataTable';
import { PillSelect } from './_ui/controls';
import { useCampaignUI, primaryButton } from './_ui/ui';
import { CAMPAIGN_STATES, statusMeta } from './_ui/status';
import { CampaignActions } from './_ui/CampaignActions';
import CampaignActionDialogs from './_ui/CampaignActionDialogs';
import usePageVisibility from './hooks/usePageVisibility';
import useDebouncedValue from './hooks/useDebouncedValue';
import useCampaignActions from './hooks/useCampaignActions';
import { campaignFromResponse, listFromResponse, count, creatorOf, formatDate, deliveryProgress, pageSummary, idOf } from './_lib/campaignState';
const PAGE_SIZE = 25;

function Identity({ campaign }) {
 const ui = useCampaignUI();
 return <HStack spacing={3} minW={0} flex={1} maxW='300px' align='flex-start'>
  <Flex boxSize='38px' borderRadius='xl' bg={ui.accentSubtle} align='center' justify='center' color={ui.textPrimary} flexShrink={0}>
   <Icon as={RiMegaphoneLine} boxSize={4} aria-hidden='true' />
  </Flex>
  <Box minW={0}><Link as={RouterLink} to={`/meta-campaigns/${idOf(campaign)}`} fontWeight='semibold' fontSize='sm'
   color={ui.textPrimary} noOfLines={2} _hover={{ textDecoration: 'underline' }}>{campaign.name || 'Untitled campaign'}</Link>
   <Text fontSize='xs' color={ui.textMuted} mt={1}>{campaign.category === 'UTILITY' ? 'Utility' : 'Marketing'}
    {' / '}{campaign.audience?.type === 'upload' ? 'Uploaded audience' : 'CRM audience'}{campaign.wave > 1 ? ` / Wave ${campaign.wave}` : ''}</Text>
  </Box>
 </HStack>;
}
function Creator({ campaign }) {
 const ui = useCampaignUI(); const creator = creatorOf(campaign);
 return <HStack spacing={2} minW='145px' maxW='200px'>
  <Avatar size='xs' name={creator.name === 'Name unavailable' ? undefined : creator.name} bg={ui.surfaceSubtle} color={ui.textSecondary} />
  <Box minW={0}><Tooltip label={creator.id ? `User ID: ${creator.id}` : 'No creator was recorded'}>
   <Text fontSize='xs' fontWeight='semibold' color={ui.textSecondary} noOfLines={1}>{creator.name}</Text>
  </Tooltip><Text fontSize='xs' color={ui.textMuted} mt={0.5}>{formatDate(campaign.createdAt)}</Text></Box>
 </HStack>;
}
function Delivery({ campaign }) {
 const ui = useCampaignUI(); const p = deliveryProgress(campaign);
 if (campaign.state === 'PLANNING') return <Text fontSize='xs' color={ui.tones.purple.fg}>Building delivery schedule...</Text>;
 if (!p.total) return <Text fontSize='xs' color={ui.textMuted}>{campaign.state === 'FAILED_TO_PLAN' ? 'Preparation incomplete' : 'Not prepared yet'}</Text>;
 return <Box minW='165px'>
  <Flex gap={2} justify='space-between' mb={1.5}><Text fontSize='xs' color={ui.textSecondary}>{count(p.submitted)} / {count(p.total)} submitted</Text><Text fontSize='xs' color={ui.textMuted}>{p.percent}%</Text></Flex>
  <Progress value={p.percent} size='xs' borderRadius='full' colorScheme='brand' bg={ui.surfaceSubtle} aria-label={`Submitted ${p.percent} percent of planned recipients`} />
  <Text fontSize='xs' color={ui.textMuted} mt={1.5}>{count(p.delivered)} delivered{p.failed ? ` / ${count(p.failed)} failed` : ''}</Text>
 </Box>;
}
function DeliveryWindow({ campaign }) {
 const ui = useCampaignUI(); const s = campaign.schedule ?? {};
 return <Box minW='135px'><Text fontSize='xs' color={ui.textSecondary}>{s.startDate ? formatDate(s.startDate) : 'Not scheduled'}</Text>
  {s.endDate && s.endDate !== s.startDate && <Text fontSize='xs' color={ui.textMuted}>to {formatDate(s.endDate)}</Text>}
  {s.windowStart && <Text fontSize='xs' color={ui.textMuted} mt={0.5}>{s.windowStart}-{s.windowEnd} recipient local time</Text>}
 </Box>;
}
export default function CampaignsList() {
 const ui = useCampaignUI(); const navigate = useNavigate(); const notify = useAppToast(); const visible = usePageVisibility();
 useMetaWhatsappSocket();
 const [state, setState] = useState(''); const [search, setSearch] = useState(''); const [page, setPage] = useState(1);
 const debouncedSearch = useDebouncedValue(search.trim());
 const query = useListCampaignsQuery({ ...(state ? { state } : {}), ...(debouncedSearch ? { search: debouncedSearch } : {}), page, pageSize: PAGE_SIZE },
  { pollingInterval: visible ? 10000 : 0, refetchOnMountOrArgChange: true, refetchOnReconnect: true });
 const manager = useCampaignActions({ refetch: query.refetch, fulfilledTimeStamp: query.fulfilledTimeStamp });
 const [create, { isLoading: creating }] = useCreateCampaignMutation(); const creatingLock = useRef(false);
 const response = listFromResponse(query.currentData ?? (query.isFetching ? undefined : query.data));
 const rows = response.rows
	.map(manager.effective)
	.filter(
		(row) =>
			!manager.deleted[
				idOf(row)
			] &&
			!row.deletedAt &&
			(
				!state ||
				row.state === state
			),
	);
 const summary = pageSummary(rows);
 useEffect(() => {
  if (!query.isFetching && response.total > 0 && page > Math.ceil(response.total / PAGE_SIZE)) setPage(Math.ceil(response.total / PAGE_SIZE));
  if (!query.isFetching && response.total === 0 && page > 1) setPage(1);
 }, [query.isFetching, response.total, page]);
 const onNew = async () => {
  if (creatingLock.current) return; creatingLock.current = true;
  try {
   const draft = campaignFromResponse(await create({ name: 'Untitled campaign' }).unwrap());
   if (!draft) throw new Error('A draft id was not returned. Refresh the list before creating another campaign.');
   navigate(`/meta-campaigns/${idOf(draft)}/edit`);
  } catch (error) { notify.error('Could not create the draft', campaignError(error)); }
  finally { creatingLock.current = false; }
 };
 const newButton = <Button leftIcon={<RiAddLine />} onClick={onNew} isLoading={creating} loadingText='Creating draft...' size='sm' {...primaryButton(ui)}>Create campaign</Button>;
 const columns = useMemo(() => [
  { key: 'name', header: 'Campaign', cell: (row) => <Identity campaign={row} /> },
  { key: 'state', header: 'Status', cell: (row) => <Box><StatusBadge status={row.state} context='campaign' />
   {row.state === 'FAILED_TO_PLAN' && <Text color={ui.danger} fontSize='xs' mt={1.5}>Open details to review</Text>}
   {row.state === 'PLANNING' && <Text color={ui.textMuted} fontSize='xs' mt={1.5}>No second launch needed</Text>}</Box> },
  { key: 'progress', header: 'Submission progress', cell: (row) => <Delivery campaign={row} /> },
  { key: 'replies', header: 'Replies', cell: (row) => <Box><Text fontWeight='semibold' fontSize='sm'>{count(row.counters?.replied)}</Text>
   <Text fontSize='xs' color={ui.textMuted}>{count(row.counters?.read)} read</Text></Box> },
  { key: 'creator', header: 'Created by / date', cell: (row) => <Creator campaign={row} /> },
  { key: 'schedule', header: 'Delivery window', cell: (row) => <DeliveryWindow campaign={row} /> },
  { key: 'actions', header: 'Actions', cell: (row) => <CampaignActions campaign={row} manager={manager} compact /> },
 ], [ui, manager]);
 const emptyAction = state || search ? <Button variant='neutralOutline' size='sm' onClick={() => { setState(''); setSearch(''); setPage(1); }}>Clear filters</Button> : newButton;
 return <CampaignPage title='WhatsApp campaigns' subtitle='One place to launch, monitor and manage every campaign.' actions={newButton}>
  <Flex justify='space-between' align='center' wrap='wrap' gap={2} mb={3}>
   <Text fontSize='sm' color={ui.textSecondary}><Text as='span' fontWeight='semibold' color={ui.textPrimary}>{count(response.total)}</Text> campaigns{state || debouncedSearch ? ' matching your filters' : ''}</Text>
   <HStack spacing={1.5}><Box boxSize='6px' borderRadius='full' bg={query.isError ? ui.warning : ui.success} /><Text fontSize='xs' color={ui.textMuted}>{query.isError ? 'Updates unavailable' : visible ? 'Status refreshes automatically' : 'Updates paused while hidden'}</Text></HStack>
  </Flex>
  <SimpleGrid columns={{ base: 2, lg: 4 }} spacing={3} mb={2}>
   <StatCard label='Active' value={summary.active} help='Releases messages when due' icon={RiPlayLine} tone='green' />
   <StatCard label='Preparing' value={summary.preparing} help='Launch already accepted' icon={RiTimeLine} tone='purple' />
   <StatCard label='Ready to launch' value={summary.ready} help='Waiting for your launch' icon={RiCheckLine} tone='blue' />
   <StatCard label='Needs a decision' value={summary.attention} help='Approval, pause or preparation issue' icon={RiErrorWarningLine} tone='amber' />
  </SimpleGrid>
  <Text fontSize='xs' color={ui.textMuted} mb={5}>These four counts reflect the {rows.length} campaigns on this page, not the entire workspace.</Text>
  <CampaignCard p={0} overflow='hidden'>
   <Box px={{ base: 3, md: 4 }} pt={4} pb={3} borderBottom='1px solid' borderColor={ui.borderSubtle}>
    <Flex gap={3} align='center' wrap='wrap' mb={3}>
     <InputGroup size='sm' maxW={{ base: '100%', md: '340px' }}>
      <InputLeftElement pointerEvents='none' color={ui.iconMuted}><RiSearchLine /></InputLeftElement>
      <Input aria-label='Search campaigns by name' placeholder='Search campaigns by name...' value={search} 
       bg={ui.inputBg} borderColor={ui.border} color={ui.textPrimary} onChange={(event) => { setSearch(event.target.value); setPage(1); }} />
     </InputGroup>
     <Text fontSize='xs' color={ui.textMuted} ml={{ base: 0, md: 'auto' }}>Creation dates use your browser's time zone.</Text>
     <Button variant='ghost' size='sm' leftIcon={<RiRefreshLine />} onClick={query.refetch} isLoading={query.isFetching && !query.isLoading} color={ui.textSecondary}>Refresh</Button>
    </Flex>
    <PillSelect label='Campaign status' value={state} onChange={(next) => { setState(next); setPage(1); }}
     options={[{ value: '', label: 'All campaigns' }, ...CAMPAIGN_STATES.map((value) => ({ value, label: statusMeta(value, 'campaign').label }))]} />
   </Box>
   <DataTable columns={columns} rows={rows} rowKey={idOf} isLoading={query.isLoading || (query.isFetching && !query.currentData)} isFetching={query.isFetching}
    isError={query.isError} onRetry={query.refetch} label='WhatsApp campaigns' minWidth='1180px'
    emptyState={{ icon: RiMegaphoneLine, title: state || search ? 'No matching campaigns' : 'Your first campaign starts here',
     description: state || search ? 'Choose another status or clear the search.' : 'Choose an audience, select an approved message, set delivery times, then review and submit. Launch only after the campaign is ready.', action: emptyAction }}
    renderMobileRow={(row) => <CampaignCard p={4} boxShadow='none'>
     <Flex justify='space-between' align='flex-start' gap={2} mb={3}><Identity campaign={row} /><StatusBadge status={row.state} context='campaign' /></Flex>
     <Delivery campaign={row} /><Flex justify='space-between' wrap='wrap' gap={3} mt={4}><Creator campaign={row} /><DeliveryWindow campaign={row} /></Flex>
     <Flex justify='space-between' align='center' wrap='wrap' gap={3} mt={4} pt={3} borderTop='1px solid' borderColor={ui.borderSubtle}>
      <Text fontSize='xs' color={ui.textMuted}>{count(row.counters?.replied)} replies</Text><CampaignActions campaign={row} manager={manager} compact />
     </Flex>
    </CampaignCard>}
    pagination={{ page, total: response.total, limit: PAGE_SIZE, onPageChange: setPage }} />
  </CampaignCard>
  <CampaignActionDialogs manager={manager} />
 </CampaignPage>;
}
